import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Landing from "@/pages/Landing";
import Terms from "@/pages/Terms";
import TermsGate from "@/components/TermsGate";
import ManagerLayout from "@/pages/manager/ManagerLayout";
import Dashboard from "@/pages/manager/Dashboard";
import Rooms from "@/pages/manager/Rooms";
import Tasks from "@/pages/manager/Tasks";
import ManagerTodo from "@/pages/manager/ManagerTodo";
import Cleaners from "@/pages/manager/Cleaners";
import Managers from "@/pages/manager/Managers";
import Timesheets from "@/pages/manager/Timesheets";
import CleanerView from "@/pages/manager/CleanerView";
import LeaveRequests from "@/pages/manager/LeaveRequests";
import Reports from "@/pages/manager/Reports";
import Timeline from "@/pages/manager/Timeline";
import Billing from "@/pages/manager/Billing";
import Maintenance from "@/pages/manager/Maintenance";
import Messages from "@/pages/manager/Messages";
import PaymentSuccess from "@/pages/manager/PaymentSuccess";
import CleanerApp from "@/pages/cleaner/CleanerApp";
import LimitedCleanerApp from "@/pages/cleaner/LimitedCleanerApp";
import Requests from "@/pages/manager/Requests";
import SuperAdmin from "@/pages/admin/SuperAdmin";
import { Loader2 } from "lucide-react";

function CleanerGate() {
  const { user } = useAuth();
  return user?.limited ? <LimitedCleanerApp /> : <CleanerApp />;
}

function RoleGate({ allow, children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  if (!allow.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  if (user === false) return <Landing />;
  if (user.role === "cleaner") return <Navigate to="/clean" replace />;
  if (user.role === "super_admin") return <Navigate to="/admin" replace />;
  return <Navigate to="/manager" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppBody />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

function AppBody() {
  const { user } = useAuth();
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  // Force a logged-in user to accept the current Terms before using the app.
  // The public /terms page stays reachable so it can be linked from anywhere.
  if (user && user !== false && user.needs_terms && path !== "/terms") {
    return <TermsGate />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/terms" element={<Terms />} />
      <Route
        path="/payment/success"
        element={
          <RoleGate allow={["manager"]}>
            <PaymentSuccess />
          </RoleGate>
        }
      />

      <Route
        path="/manager"
        element={
          <RoleGate allow={["manager"]}>
            <ManagerLayout />
          </RoleGate>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="rooms" element={<Rooms />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="todo" element={<ManagerTodo />} />
        <Route path="cleaners" element={<Cleaners />} />
        <Route path="managers" element={<Managers />} />
        <Route path="timesheets" element={<Timesheets />} />
        <Route path="view-as" element={<CleanerView />} />
        <Route path="leave" element={<LeaveRequests />} />
        <Route path="reports" element={<Reports />} />
        <Route path="timeline" element={<Timeline />} />
        <Route path="maintenance" element={<Maintenance />} />
        <Route path="messages" element={<Messages />} />
        <Route path="billing" element={<Billing />} />
        <Route path="requests" element={<Requests />} />
      </Route>

      <Route
        path="/clean"
        element={
          <RoleGate allow={["cleaner", "manager"]}>
            <CleanerGate />
          </RoleGate>
        }
      />

      <Route
        path="/admin"
        element={
          <RoleGate allow={["super_admin"]}>
            <SuperAdmin />
          </RoleGate>
        }
      />
    </Routes>
  );
}

export default App;
